import { Box, Chip, Grid } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useColors from "../../../../api/hooks/useColors";
import CustomTooltipBorder from "../../../../components/CustomTooltipBorder";
import { QueryParam } from "../../../../utils/constants";
import RelatedAssetChip from "../../../stack/assets/components/RelatedAssetChip";

const CHIPS_GAP = 8;
const HIDDEN_ASSETS_CHIP_WIDTH = 50;

const AffectedAssetsCell = ({ assets }) => {
  const colors = useColors();
  const navigate = useNavigate();
  const handleAssetChipClick = (asset) => {
    navigate(`/inventory/assets?${QueryParam.id}=${asset.asset_id}`);
  };

  const [cellWidth, setCellWidth] = useState(0);
  const [widthsOfChips, setWidthsOfChips] = useState([]);
  const cellRef = useRef(null);
  const hiddenRef = useRef(null);
  const chipRefs = useRef([]);
  const orderedAssets = [...assets].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === cellRef.current) {
          const newWidth = entry.contentRect.width;
          setCellWidth(newWidth);
        }
      });
    });

    if (cellRef.current) {
      resizeObserver.observe(cellRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    // Calculate widths of chips from hidden box
    if (hiddenRef.current && cellWidth > 0) {
      const calculatedWidths = Array.from(hiddenRef.current.children).map(
        (child) => child.offsetWidth
      );

      // If there's only one asset, always show it
      if (calculatedWidths.length === 1) {
        setWidthsOfChips([calculatedWidths[0]]);
        return;
      }

      // Account for padding and gaps in the calculation
      const containerPadding = 8; // 0.5 * 16 = 8px padding on each side
      const availableWidth = cellWidth - containerPadding;

      // Update widths based on cellWidth visibility
      let totalWidth = 0;
      const updatedWidths = calculatedWidths.map((width, index) => {
        const gapWidth = index > 0 ? CHIPS_GAP : 0; // Add gap for all chips except the first
        const requiredWidth = totalWidth + width + gapWidth;

        if (requiredWidth <= availableWidth) {
          totalWidth = requiredWidth;
          return width;
        }
        return 0; // 0 if not visible
      });

      // Ensure at least one asset is always visible (show the first one if none fit)
      const visibleCount = updatedWidths.filter((width) => width > 0).length;
      if (visibleCount === 0 && calculatedWidths.length > 0) {
        updatedWidths[0] = calculatedWidths[0]; // Force the first asset to be visible
      }

      setWidthsOfChips(updatedWidths);
    }
  }, [cellWidth, assets]);

  const hiddenAssets = orderedAssets.filter(
    (_, index) => widthsOfChips[index] === 0
  );

  return (
    <Box
      ref={cellRef}
      padding={0.5}
      sx={{
        display: "flex",
        gap: `${CHIPS_GAP}px`,
        overflow: "hidden",
        whiteSpace: "nowrap",
        height: "24px", // Adjust the height as needed
        alignItems: "center",
      }}
    >
      {/* Hidden Box with assets chips for width calculation */}
      <Box
        ref={hiddenRef}
        sx={{
          position: "absolute",
          visibility: "hidden",
          height: 0,
          overflow: "hidden",
        }}
      >
        {orderedAssets.map(
          (asset, index) =>
            asset && (
              <Box
                key={asset.asset_id}
                ref={(el) => {
                  chipRefs.current[index] = el;
                }}
              >
                <RelatedAssetChip
                  asset={asset}
                  handleClick={() => handleAssetChipClick(asset)}
                />
              </Box>
            )
        )}
      </Box>
      {orderedAssets.map((asset, index) => {
        if (!widthsOfChips[index] || widthsOfChips[index] === 0) return null;
        return (
          <Box key={asset.asset_id}>
            <RelatedAssetChip
              asset={asset}
              handleClick={() => handleAssetChipClick(asset)}
            />
          </Box>
        );
      })}
      {/* Additional "+" chip for undisplayed asset */}
      {hiddenAssets.length > 0 && (
        <CustomTooltipBorder
          title={
            <Grid container direction="column" gap={0.5}>
              {hiddenAssets.map((asset, index) => (
                <Grid
                  item
                  key={asset.asset_id}
                  onClick={() => handleAssetChipClick(asset)}
                  sx={{ cursor: "pointer", userSelect: "none" }}
                >
                  {asset.name}
                  {index < hiddenAssets.length - 1}
                </Grid>
              ))}
            </Grid>
          }
          disableHoverListener={false}
          borderColor={colors.common_white}
          placement={"bottom"}
        >
          <Chip
            label={`+${hiddenAssets.length}`}
            style={{
              minWidth: HIDDEN_ASSETS_CHIP_WIDTH,
              background: colors.critical_asset_chip_enabled_fill,
              boxShadow: colors.critical_asset_chip_enabled_shadow,
              height: "22px",
              borderRadius: "8px",
              fontSize: "12px",
              color: colors.text_primary,
              cursor: "auto",
              userSelect: "none",
            }}
          />
        </CustomTooltipBorder>
      )}
    </Box>
  );
};

export default AffectedAssetsCell;
